import { useState, useEffect, useCallback } from 'react';
import { useIntl } from 'react-intl';
import { Text, Button, Badge, Spinner, Card, CardHeader } from '@fluentui/react-components';
import { ArrowLeftRegular, ArrowSyncRegular, MoneyRegular, ErrorCircleRegular } from '@fluentui/react-icons';
import { generateEstimate, getCachedEstimate, type EngagementCostEstimate, type CostCategory } from '../services/cost-service';
import { useApiToken } from '../hooks/useApiToken';
import type { Project } from '../services/project-service';

interface CostEstimatePageProps {
  project: Project;
  onBack: () => void;
}

export function CostEstimatePage({ project, onBack }: CostEstimatePageProps) {
  const intl = useIntl();
  const [estimate, setEstimate] = useState<EngagementCostEstimate | null>(null);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const getToken = useApiToken();

  const loadCached = useCallback(async () => {
    if (!project.id) return;
    setLoading(true);
    try {
      const token = await getToken();
      if (!token) return;
      const cached = await getCachedEstimate(project.id, project.customerId, token);
      setEstimate(cached);
    } catch {
      // No cached estimate yet
    } finally {
      setLoading(false);
    }
  }, [project.id, project.customerId, getToken]);

  useEffect(() => { loadCached(); }, [loadCached]);

  const handleGenerate = useCallback(async () => {
    if (!project.id) return;
    setError(null);
    setGenerating(true);
    try {
      const token = await getToken();
      if (!token) return;
      const result = await generateEstimate(
        project.id, project.customerId, token,
        project.azureRegion ?? 'southafricanorth', 'USD', '1yr'
      );
      setEstimate(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate estimate.');
    } finally {
      setGenerating(false);
    }
  }, [project.id, project.customerId, project.azureRegion, getToken]);

  const fmt = (n: number) => `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  return (
    <div className="page-root page-root--scrollable">
      <div className="page-title-bar">
        <Button appearance="subtle" icon={<ArrowLeftRegular />} onClick={onBack} title={intl.formatMessage({ id: 'costEstimate.backButton.title' })} />
        <MoneyRegular className="icon-title text-brand" />
        <Text size={500} weight="semibold">{intl.formatMessage({ id: 'costEstimate.title' }, { name: project.name })}</Text>
        <div className="title-bar-actions">
          <Button
            appearance="primary"
            icon={<ArrowSyncRegular />}
            onClick={handleGenerate}
            disabled={generating}
            size="small"
          >
            {generating
              ? intl.formatMessage({ id: 'costEstimate.generateButton.generating' })
              : estimate
                ? intl.formatMessage({ id: 'costEstimate.generateButton.refresh' })
                : intl.formatMessage({ id: 'costEstimate.generateButton.generate' })}
          </Button>
        </div>
      </div>

      {loading && (
        <div className="page-loading">
          <Spinner size="large" label={intl.formatMessage({ id: 'costEstimate.loading' })} />
        </div>
      )}

      {error && (
        <div className="page-error">
          <ErrorCircleRegular />
          <Text className="text-danger">{error}</Text>
          <Button appearance="primary" onClick={handleGenerate}>{intl.formatMessage({ id: 'costEstimate.retryButton' })}</Button>
        </div>
      )}

      {!loading && !estimate && (
        <div className="page-empty">
          <MoneyRegular />
          <Text className="text-muted">
            {intl.formatMessage({ id: 'costEstimate.empty' })}
          </Text>
        </div>
      )}

      {estimate && (
        <div className="snapshots-section">
          {/* Summary cards */}
          <div className="dashboard-summary">
            <SummaryCard label={intl.formatMessage({ id: 'costEstimate.summary.azureConsumption' })} value={fmt(estimate.azureConsumptionMonthly)} sublabel={intl.formatMessage({ id: 'costEstimate.summary.perMonth' })} colour="brand" />
            <SummaryCard label={intl.formatMessage({ id: 'costEstimate.summary.w365Licensing' })} value={fmt(estimate.w365LicensingMonthly)} sublabel={intl.formatMessage({ id: 'costEstimate.summary.perMonth' })} colour="informative" />
            <SummaryCard label={intl.formatMessage({ id: 'costEstimate.summary.grandTotal' })} value={fmt(estimate.grandTotalMonthly)} sublabel={intl.formatMessage({ id: 'costEstimate.summary.perMonth' })} colour="success" />
            <SummaryCard label={intl.formatMessage({ id: 'costEstimate.summary.savingsPlan' })} value={estimate.savingsPlanPhase.toUpperCase()} sublabel={intl.formatMessage({ id: 'costEstimate.summary.pricingTier' })} colour="warning" />
          </div>

          {/* Category breakdowns */}
          {estimate.categories.map(cat => (
            <CategoryTable key={cat.name} category={cat} fmt={fmt} />
          ))}

          {/* Notes */}
          {estimate.notes.length > 0 && (
            <div className="text-muted cost-estimate-notes mt-md">
              {estimate.notes.map((n, i) => <div key={i}>* {n}</div>)}
            </div>
          )}

          <div className="text-subtle cost-estimate-meta mt-sm">
            {intl.formatMessage({ id: 'costEstimate.generatedAt' }, { date: new Date(estimate.generatedAt).toLocaleString() })}
          </div>
        </div>
      )}
    </div>
  );
}

function SummaryCard({ label, value, sublabel, colour }: {
  label: string; value: string; sublabel: string; colour: 'brand' | 'success' | 'warning' | 'informative';
}) {
  const textColour = colour === 'informative' ? 'text-brand' : `text-${colour}`;
  return (
    <Card size="small" className="summary-card">
      <CardHeader
        header={<Text size={200} weight="semibold" className="text-muted">{label}</Text>}
        description={<Text size={600} weight="bold" className={textColour}>{value}</Text>}
      />
      <div className="summary-card-detail">
        <Text size={200} className="text-subtle">{sublabel}</Text>
      </div>
    </Card>
  );
}

function CategoryTable({ category, fmt }: { category: CostCategory; fmt: (n: number) => string }) {
  const intl = useIntl();
  return (
    <div className="cost-estimate-category">
      <div className="section-header">
        <Text size={400} weight="semibold">{category.name}</Text>
        <Badge appearance="outline" size="small">{fmt(category.subtotalMonthly)}{intl.formatMessage({ id: 'costEstimate.table.perMonthSuffix' })}</Badge>
      </div>
      <div className="table-scroll-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>{intl.formatMessage({ id: 'costEstimate.table.resource' })}</th>
              <th>{intl.formatMessage({ id: 'costEstimate.table.sku' })}</th>
              <th>{intl.formatMessage({ id: 'costEstimate.table.qty' })}</th>
              <th>{intl.formatMessage({ id: 'costEstimate.table.unitPrice' })}</th>
              <th>{intl.formatMessage({ id: 'costEstimate.table.monthly' })}</th>
              <th>{intl.formatMessage({ id: 'costEstimate.table.pricing' })}</th>
            </tr>
          </thead>
          <tbody>
            {category.lineItems.map((item, i) => (
              <tr key={i}>
                <td>{item.name}</td>
                <td className="cost-estimate-sku">{item.sku}</td>
                <td>{item.quantity}</td>
                <td>{fmt(item.unitPriceMonthly)}</td>
                <td><strong>{fmt(item.totalMonthly)}</strong></td>
                <td><Badge size="small" appearance="outline">{item.pricingTier}</Badge></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
